/**
 * SettingsDialog — the "account & profile" preferences popup opened from the
 * user menu. Lets the super-user pick the UI language (Català / Español /
 * English) and the theme (Light / Dark). Token-driven (Light + Dark via
 * @obrava/ui semantic tokens); all copy via i18n. Overlay/scrim pattern shared
 * with ReasonDialog.
 */

import React from 'react';
import { Button, useTheme, type ThemeName } from '@obrava/ui';
import { useTranslation } from 'react-i18next';
import { APP_LANGUAGES, type AppLanguage } from '@obrava/shared';
import { useLanguage } from '../providers/I18nProvider.js';
import { useThemeControl } from '../providers/ThemeProvider.js';

/** A compact segmented control: one active option, token-driven. */
function Segmented<T extends string>(props: {
  readonly label: string;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly value: T;
  readonly onChange: (value: T) => void;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span
        style={{
          fontFamily: theme.typography.family.sans,
          fontSize: theme.typography.size.footnote,
          fontWeight: theme.typography.weight.medium,
          color: theme.color.textSecondary,
          marginBottom: theme.spacing.space[2],
        }}
      >
        {props.label}
      </span>
      <div
        style={{
          display: 'flex',
          gap: theme.spacing.space[1],
          padding: 3,
          backgroundColor: theme.color.surfaceSunken,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radii.radius.field,
        }}
      >
        {props.options.map((opt) => {
          const active = opt.value === props.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => props.onChange(opt.value)}
              aria-pressed={active}
              style={{
                flex: 1,
                padding: `${theme.spacing.space[2]}px ${theme.spacing.space[3]}px`,
                border: 'none',
                borderRadius: theme.radii.radius.sm,
                backgroundColor: active ? theme.color.primarySubtle : 'transparent',
                color: active ? theme.color.primary : theme.color.textSecondary,
                fontFamily: theme.typography.family.sans,
                fontSize: theme.typography.size.footnote,
                fontWeight: active
                  ? theme.typography.weight.semibold
                  : theme.typography.weight.medium,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SettingsDialog(props: {
  readonly open: boolean;
  readonly onClose: () => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const theme = useTheme();
  const { language, setLanguage } = useLanguage();
  const { scheme, setScheme } = useThemeControl();

  React.useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.open, props]);

  if (!props.open) return null;

  const langOptions: readonly { value: AppLanguage; label: string }[] = APP_LANGUAGES.map(
    (l) => ({ value: l, label: l.toUpperCase() }),
  );
  const themeOptions: readonly { value: ThemeName; label: string }[] = [
    { value: 'light', label: t('platformConsole.common:themeLight') },
    { value: 'dark', label: t('platformConsole.common:themeDark') },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('platformConsole.common:account')}
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.color.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing.space[6],
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: theme.spacing.size.dialogMaxWidth,
          backgroundColor: theme.color.surfaceOverlay,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radii.radius.card,
          boxShadow: theme.radii.shadow.overlay,
          padding: theme.spacing.space[7],
        }}
      >
        <h2
          style={{
            margin: `0 0 ${theme.spacing.space[6]}px`,
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.title3,
            fontWeight: theme.typography.weight.semibold,
            color: theme.color.textPrimary,
          }}
        >
          {t('platformConsole.common:account')}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[6] }}>
          <Segmented
            label={t('platformConsole.common:language')}
            options={langOptions}
            value={language}
            onChange={setLanguage}
          />
          <Segmented
            label={t('platformConsole.common:theme')}
            options={themeOptions}
            value={scheme}
            onChange={setScheme}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: theme.spacing.space[7] }}>
          <Button onClick={props.onClose}>{t('common:close')}</Button>
        </div>
      </div>
    </div>
  );
}
